import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { CreateClaimRecordPayload, CreateClaimRecordResult } from '@typesApp/ClaimRecord'
import { Customer } from '@typesApp/Customer'
import { CartBundle } from '@typesApp/Product'
import { useCreateClaimRecordMutation, useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { connectorAfterCreateClaimRecord } from '@redux/slices/connectorSlice'
import { isGatewayTimeout } from '@utils/errorUtil'
import { useActiveData } from '../useActiveData'
import { useCart } from '../useCart'
import { useCustomer } from '../useCustomer'
import { useEnvironment } from '../useEnvironment'
import { useSessionInfo } from '../useSessionInfo'
import { createClaimRecordController } from './createClaimRecordController'

type CreateOptions = {
  insurerId?: string
  cartBundle?: CartBundle
  customer?: Customer
}

type UseCreateClaimRecordReturn = {
  isError: boolean
  createClaimRecord: (options?: CreateOptions) => Promise<CreateClaimRecordResult> | never
}

export const useCreateClaimRecord = (): UseCreateClaimRecordReturn => {
  const dispatch = useDispatch()
  const { ecommerceCode, ecommerceTransactionHost, lang, country, channel } = useEnvironment()
  const { customerContext } = useSessionInfo()

  const { fetchCartBundle } = useCart()
  const { fetchActiveData } = useActiveData()
  const { validateCustomerOrThrow } = useCustomer()

  const [fetchClaimRecord] = useLazyGetClaimRecordQuery()
  const [putClaimRecord, { isError }] = useCreateClaimRecordMutation()

  const controller = useMemo(
    () =>
      createClaimRecordController(
        {
          fetchCartBundle,
          putClaimRecord: (payload: CreateClaimRecordPayload) => putClaimRecord(payload).unwrap(),
        },
        {
          ecommerceCode,
          ecommerceTransactionHost,
          lang,
          country,
          channel,
        },
        customerContext
      ),
    [fetchCartBundle, putClaimRecord, ecommerceCode, ecommerceTransactionHost, lang, country, channel, customerContext]
  )

  const createClaimRecordWithCustomer = useCallback(
    async (customer: Customer, options?: CreateOptions): Promise<CreateClaimRecordResult> | never => {
      try {
        const result = await controller.createClaimRecord({ customer, options })
        dispatch(connectorAfterCreateClaimRecord())
        return result
      } catch (error) {
        if (isGatewayTimeout(error)) {
          const activeData = await fetchActiveData(customer?.id)
          if (activeData?.id) {
            dispatch(connectorAfterCreateClaimRecord())
            return await fetchClaimRecord(activeData.id).unwrap()
          }
        }
        throw error
      }
    },
    [dispatch, fetchActiveData, fetchClaimRecord, controller]
  )

  const createClaimRecord = useCallback(
    async (options?: CreateOptions): Promise<CreateClaimRecordResult> | never => {
      // skip validate customer if caller provides customer object
      const customer = options?.customer ?? (await validateCustomerOrThrow())

      return createClaimRecordWithCustomer(customer, options)
    },
    [validateCustomerOrThrow, createClaimRecordWithCustomer]
  )

  return {
    isError,
    createClaimRecord,
  }
}
